"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useOptionalDependencies = exports.useDependencies = exports.injectDependency = exports.Provider = exports.provide = exports.symbolToken = void 0;
const react_1 = __importDefault(require("react"));
const inversify_1 = require("inversify");
const container_context_1 = require("./container-context");
require("reflect-metadata");
function symbolToken(key) {
    return Symbol.for(key);
}
exports.symbolToken = symbolToken;
function isClassProviderConfigEntry(entry) {
    return (Object.prototype.hasOwnProperty.call(entry, 'provide') &&
        Object.prototype.hasOwnProperty.call(entry, 'useClass'));
}
function isValueProviderConfigEntry(entry) {
    return (Object.prototype.hasOwnProperty.call(entry, 'provide') &&
        Object.prototype.hasOwnProperty.call(entry, 'useValue'));
}
exports.provide = (stores => (Component) => props => (react_1.default.createElement(container_context_1.ContainerContext.Consumer, null, container => (react_1.default.createElement(ProviderComponent, Object.assign({}, stores, { parentContainer: container }),
    react_1.default.createElement(Component, Object.assign({}, props)))))));
exports.Provider = props => (react_1.default.createElement(container_context_1.ContainerContext.Consumer, null, container => (react_1.default.createElement(ProviderComponent, Object.assign({}, props, { parentContainer: container }), props.children))));
class ProviderComponent extends react_1.default.Component {
    constructor(props) {
        super(props);
        this.bind = (entry) => {
            if (isClassProviderConfigEntry(entry)) {
                return this.container.bind(entry.provide).to(entry.useClass);
            }
            if (isValueProviderConfigEntry(entry)) {
                // using toDynamicValue to return BindingInSyntax
                return this.container.bind(entry.provide).toDynamicValue(() => entry.useValue);
            }
            return this.container.bind(entry).to(entry);
        };
        this.unbind = (entry) => {
            if (isClassProviderConfigEntry(entry) || isValueProviderConfigEntry(entry)) {
                return this.container.unbind(entry.provide);
            }
            return this.container.unbind(entry);
        };
        this.container = new inversify_1.Container();
        this.container.parent = this.props.parentContainer;
        if (this.props.singletons) {
            this.props.singletons.map(this.bind).forEach(binding => {
                binding.inSingletonScope();
            });
        }
        if (this.props.instances) {
            this.props.instances.forEach(this.bind);
        }
    }
    componentWillUnmount() {
        if (this.props.singletons) {
            this.props.singletons.forEach(this.unbind);
        }
        if (this.props.instances) {
            this.props.instances.forEach(this.unbind);
        }
    }
    render() {
        return (react_1.default.createElement(container_context_1.ContainerContext.Provider, { value: this.container }, this.props.children));
    }
}
const INJECTION = Symbol.for('INJECTION');
function proxyGetter(proto, key, resolve, doCache) {
    function getter() {
        if (doCache && !Reflect.hasMetadata(INJECTION, this, key)) {
            Reflect.defineMetadata(INJECTION, resolve(this), this, key);
        }
        if (Reflect.hasMetadata(INJECTION, this, key)) {
            return Reflect.getMetadata(INJECTION, this, key);
        }
        return resolve(this);
    }
    function setter(newVal) {
        Reflect.defineMetadata(INJECTION, newVal, this, key);
    }
    Object.defineProperty(proto, key, {
        configurable: true,
        enumerable: true,
        get: getter,
        set: setter,
    });
}
function injectDependency(serviceIdentifier) {
    return function (proto, key) {
        const resolve = (componentInstance) => {
            var _a;
            if (!componentInstance.context) {
                throw new Error("Context doesn't exist on a target class. " +
                    'Please make sure to pass context object to super method if you access injectable stores in constructor.');
            }
            const metadata = Reflect.getMetadata(inversify_1.METADATA_KEY.TAGGED_PROP, proto.constructor) || {};
            if ((_a = metadata[key]) === null || _a === void 0 ? void 0 : _a.some(({ key, value }) => key === inversify_1.METADATA_KEY.OPTIONAL_TAG && value)) {
                if (componentInstance.context.isBound(serviceIdentifier)) {
                    return componentInstance.context.get(serviceIdentifier);
                }
            }
            else {
                return componentInstance.context.get(serviceIdentifier);
            }
        };
        if (proto.constructor.contextType && proto.constructor.contextType !== container_context_1.ContainerContext) {
            throw new Error("injectDependency can't be used together with contextType static property");
        }
        proto.constructor.contextType = container_context_1.ContainerContext;
        proxyGetter(proto, key, resolve, true);
    };
}
exports.injectDependency = injectDependency;
function useDependenciesInternal(tokens, optional) {
    const ref = react_1.default.useRef(null);
    const container = react_1.default.useContext(container_context_1.ContainerContext);
    if (!ref.current) {
        ref.current = tokens.map(s => {
            if (optional) {
                if (container.isBound(s)) {
                    return container.get(s);
                }
                return undefined;
            }
            return container.get(s);
        });
    }
    return ref.current;
}
function useDependencies(...tokens) {
    return useDependenciesInternal(tokens, false);
}
exports.useDependencies = useDependencies;
function useOptionalDependencies(...tokens) {
    return useDependenciesInternal(tokens, true);
}
exports.useOptionalDependencies = useOptionalDependencies;
//# sourceMappingURL=react-ioc.js.map